// Performance monitoring utility
import { onCLS, onINP, onLCP, onTTFB, onFCP } from 'web-vitals';
import logger from './logger';
import { isPublicPath } from './publicPaths';

// Performance marks for tracking load times
const performanceMarks = new Map<string, number>();

// Track component mount times
export const markComponentMount = (componentName: string) => {
  const startTime = performance.now();
  performanceMarks.set(`${componentName}_start`, startTime);
  
  return () => {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (duration > 1000) {
      logger.warn(`Slow component mount: ${componentName} took ${duration.toFixed(2)}ms`);
    }
    
    return duration;
  };
};

// Track API call times
export const trackApiCall = async <T>(
  apiName: string,
  apiCall: () => Promise<T>
): Promise<T> => {
  const startTime = performance.now();
  
  try {
    const result = await apiCall();
    const duration = performance.now() - startTime;
    
    if (duration > 2000) {
      logger.warn(`Slow API call: ${apiName} took ${duration.toFixed(2)}ms`);
    } else if (duration > 1000) {
      logger.debug(`API call: ${apiName} took ${duration.toFixed(2)}ms`);
    }
    
    // Track in performance marks
    performanceMarks.set(`api_${apiName}`, duration);
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    logger.error(`API call failed: ${apiName} after ${duration.toFixed(2)}ms`, error);
    throw error;
  }
};

// Report Web Vitals
export const reportWebVitals = (onPerfEntry?: (metric: {name: string; value: number; rating: 'good' | 'needs-improvement' | 'poor'; id: string}) => void) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    onCLS(onPerfEntry);
    onINP(onPerfEntry); // INP (Interaction to Next Paint) replaced FID
    onLCP(onPerfEntry);
    onFCP(onPerfEntry);
    onTTFB(onPerfEntry);
  }
};

// Log Web Vitals to console with color coding
export const logWebVitals = () => {
  const logVital = (metric: {name: string; value: number; rating: 'good' | 'needs-improvement' | 'poor'; id: string}) => {
    const { name, value, rating } = metric;
    if (rating === 'good') {
      logger.info(`[WebVitals] ${name}: ${value.toFixed(2)}ms (${rating})`);
    } else if (rating === 'needs-improvement') {
      logger.warn(`[WebVitals] ${name}: ${value.toFixed(2)}ms (${rating})`);
    } else {
      logger.error(`[WebVitals] ${name}: ${value.toFixed(2)}ms (${rating})`);
    }
    
    // Send to analytics only on public pages (Web Vitals would otherwise leak
    // page_location of authenticated routes once GA is loaded in the SPA session).
    if ((window as any).gtag && isPublicPath(window.location.pathname)) {
      (window as any).gtag('event', name, {
        value: Math.round(value),
        event_category: 'Web Vitals',
        event_label: rating,
      });
    }
  };
  
  onCLS(logVital);
  onINP(logVital); // INP replaced FID
  onLCP(logVital);
  onFCP(logVital);
  onTTFB(logVital);
};

// Get performance summary
export const getPerformanceSummary = () => {
  const entries: Record<string, number> = {};
  
  performanceMarks.forEach((value, key) => {
    entries[key] = value;
  });
  
  // Add navigation timing
  if (performance.timing) {
    const { navigationStart, loadEventEnd, domContentLoadedEventEnd } = performance.timing;
    entries['page_load'] = loadEventEnd - navigationStart;
    entries['dom_ready'] = domContentLoadedEventEnd - navigationStart;
  }
  
  return entries;
};

// Track route changes
export const trackRouteChange = (pathname: string) => {
  const timestamp = performance.now();
  performanceMarks.set(`route_${pathname}`, timestamp);
  
  // Log if this is a slow route
  const lastRoute = Array.from(performanceMarks.entries())
    .filter(([key]) => key.startsWith('route_'))
    .sort(([, a], [, b]) => b - a)[1];
    
  if (lastRoute) {
    const duration = timestamp - lastRoute[1];
    if (duration > 500) {
      logger.debug(`Route change took ${duration.toFixed(2)}ms to ${pathname}`);
    }
  }
};

// Clear performance data
export const clearPerformanceData = () => {
  performanceMarks.clear();
};

// Export for debugging
if ((typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') || import.meta.env?.MODE === 'development') {
  (window as any).__perf = {
    getMarks: () => performanceMarks,
    getSummary: getPerformanceSummary,
    clear: clearPerformanceData
  };
}